import AsyncFormInput from '@form/AsyncFormInput';
import { useClubSlug } from 'components/Clubs/hooks';
import { Column } from 'components/DataTable';
import { snakeCase } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface UserSelectionInputProps {
    prefix: string;
    clubSlug?: string;
    /** trainingsjournal.recommendations_only — read once by the parent, not per row. */
    recommendationsOnly?: boolean;
}

// "users.3" -> { arrayName: "users", index: 3 }
const splitPrefix = (prefix: string) => {
    const match = prefix.match(/^(.*)\.(\d+)$/);

    return { arrayName: match?.[1] ?? prefix, index: match ? Number(match[2]) : -1 };
};

export default function UserSelectionInput({
    prefix,
    clubSlug: _clubSlug,
    // when the club forbids free-text participants, a typed value that was never confirmed
    // from the dropdown is rolled back on blur — the backend rejects it either way
    recommendationsOnly
}: UserSelectionInputProps) {
    const { t } = useTranslation('trainingsjournal');
    const clubSlug = useClubSlug(_clubSlug);
    const { setValue, control } = useFormContext();
    const { arrayName, index } = splitPrefix(prefix);

    const roster = useWatch({ control, name: arrayName });
    // the last pair actually picked from the suggestions, used to restore a typed-over field
    const confirmed = useRef<{ user_name: string; user_email: string } | null | undefined>(undefined);

    // participants already stored on the entry count as confirmed — they predate the setting
    // and the backend exempts them too, so editing an unrelated field must not wipe them
    if (confirmed.current === undefined) {
        const initial = roster?.[index];
        confirmed.current = initial?.user_email
            ? { user_name: initial.user_name, user_email: initial.user_email }
            : null;
    }

    const columns: Column[] = [
        { uid: 'user_name', name: t('common.userSelection.username') },
        { uid: 'user_email', name: t('common.userSelection.email') }
    ];

    // people already on the roster, so the same person cannot be picked twice; the own row is
    // left out or typing in it would filter away its own suggestions.
    // Name AND address, never the address alone: siblings on a parent's mail and whole groups
    // on a club address are normal here, and the backend's duplicate check judges the pair
    // too — excluding by address would make the second sibling unpickable.
    const excluded = useMemo(
        () =>
            (Array.isArray(roster) ? roster : [])
                .filter((_user, i) => i !== index)
                .map((user) => ({
                    user_email: String(user?.user_email ?? '')
                        .trim()
                        .toLowerCase(),
                    user_name: String(user?.user_name ?? '')
                        .trim()
                        .toLowerCase()
                }))
                .filter((user) => !!user.user_email)
                .sort((a, b) => `${a.user_email} ${a.user_name}`.localeCompare(`${b.user_email} ${b.user_name}`)),
        [roster, index]
    );

    const fetchUrl = useMemo(() => {
        const base = `club/${clubSlug}/journal/user/suggestions`;
        if (excluded.length === 0) return base;

        const params = new URLSearchParams();
        excluded.forEach((user, i) => {
            params.append(`exclude[${i}][user_email]`, user.user_email);
            params.append(`exclude[${i}][user_name]`, user.user_name);
        });

        return `${base}?${params.toString()}`;
    }, [clubSlug, excluded]);

    const handleSetValue = useCallback(
        (value?: { user_email: string; user_name: string }) => {
            if (!value) return;

            confirmed.current = { user_name: value.user_name, user_email: value.user_email };
            setValue(`${prefix}.user_email`, value.user_email, { shouldDirty: true, shouldValidate: true });
            setValue(`${prefix}.user_name`, value.user_name, { shouldDirty: true, shouldValidate: true });
        },
        [prefix, setValue]
    );

    // rolls both fields back to the last confirmed suggestion once the user leaves the input
    const handleBlur = useCallback(() => {
        if (!recommendationsOnly) return;

        const current = roster?.[index];
        const isConfirmed =
            confirmed.current &&
            confirmed.current.user_email === current?.user_email &&
            confirmed.current.user_name === current?.user_name;

        if (isConfirmed) return;

        setValue(`${prefix}.user_email`, confirmed.current?.user_email ?? '', { shouldValidate: true });
        setValue(`${prefix}.user_name`, confirmed.current?.user_name ?? '', { shouldValidate: true });
    }, [recommendationsOnly, roster, index, prefix, setValue]);

    const inputProps = { flush: true, onBlur: handleBlur };

    return (
        <div className="tw-grid tw-grid-cols-2 tw-items-start tw-gap-4">
            <AsyncFormInput
                tableColumns={columns}
                fetchUrl={fetchUrl}
                label={t('common.userSelection.username')}
                name={`${prefix}.user_name`}
                inputProps={inputProps}
                keyResolver={(value: any) => `${snakeCase(value.user_name)}_${snakeCase(value.user_email)}`}
                onSelect={handleSetValue}
            />
            <AsyncFormInput
                tableColumns={columns}
                fetchUrl={fetchUrl}
                label={t('common.userSelection.email')}
                name={`${prefix}.user_email`}
                inputProps={inputProps}
                keyResolver={(value: any) => `${snakeCase(value.user_name)}_${snakeCase(value.user_email)}`}
                onSelect={handleSetValue}
            />
        </div>
    );
}
